import type { UserMedia, StaffLanguage } from '../types';
import type { MediaListCollection } from '../types/anilist';
import { mapUserMedia, mapCharacter } from './mapUserMedia';

const ANILIST_API_URL = 'https://graphql.anilist.co';

interface AniListVariables {
    type: string;
    userName: string;
    language: StaffLanguage | string;
    sort: string[];
    page: number;
}

const characterQueryPart = `
          characters(sort: $sort, page: $page) {
            edges {
              role
              node {
                id
                image {
                  medium
                }
                name {
                  full
                }
              }
              voiceActors(language: $language) {
                id
                name {
                  full
                }
                image {
                  medium
                }
              }
            }
          }
`;

const getAdditionalCharactersQuery = `
query ($type: MediaType!, $userName: String, $language: StaffLanguage, $sort: [CharacterSort], $page: Int) {
  MediaListCollection(type: $type, userName: $userName) {
    hasNextChunk
    lists {
      name
      entries {
        id
        media {
          id
          ${characterQueryPart}
        }
      }
    }
  }
}
`;

const getMediaListsQuery = `
query ($type: MediaType!, $userName: String, $language: StaffLanguage, $sort: [CharacterSort], $page: Int) {
  MediaListCollection(type: $type, userName: $userName) {
    hasNextChunk
    lists {
      name
      isCustomList
      entries {
        id
        status
        score
        progress
        repeat
        media {
          id
          title {
            english
            romaji
          }
          coverImage {
            medium
            large
          }
          bannerImage
          description
          averageScore
          popularity
          episodes
          genres
          ${characterQueryPart}
        }
      }
    }
  }
}
`;

const fetchMediaLists = async (userName: string, language: StaffLanguage): Promise<MediaListCollection> => {
    const variables: AniListVariables = {
        type: "ANIME",
        userName,
        language: language.toUpperCase(),
        sort: ["ROLE", "RELEVANCE", "ID"],
        page: 1
    };

    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
        body: JSON.stringify({
            query: getMediaListsQuery,
            variables: variables
        })
    };

    const response = await fetch(ANILIST_API_URL, options);
    if (!response.ok) {
        throw new Error(`AniList API error: ${response.statusText}`);
    }
    const data = await response.json();
    if (data.errors) {
        throw new Error(`AniList API error: ${data.errors[0].message}`);
    }
    return data.data.MediaListCollection as MediaListCollection;
};

const fetchAdditionalCharacters = async (userName: string, language: StaffLanguage): Promise<MediaListCollection[]> => {
    const variables: AniListVariables = {
        type: "ANIME",
        userName,
        language: language.toUpperCase(),
        sort: ["ROLE", "RELEVANCE", "ID"],
        page: 1
    };

    let cont = true,
        page = 1,
        res: MediaListCollection[] = [];
    do {
        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({
                query: getAdditionalCharactersQuery,
                variables: { ...variables, page: ++page }
            })
        };

        const response = await fetch(ANILIST_API_URL, options);
        if (!response.ok) {
            throw new Error(`AniList API error: ${response.statusText}`);
        }
        const data = await response.json();
        if (data.errors) {
            throw new Error(`AniList API error: ${data.errors[0].message}`);
        }
        const mlc = data.data.MediaListCollection as MediaListCollection;
        res.push(mlc);

    } while (cont && page < 5);

    return res;
}

export const aniListService = {
    fetchUserMedia: async (userName: string, language: StaffLanguage): Promise<UserMedia[]> => {
        const lookup = {} as Record<number, UserMedia>;
        const mediaLists = await fetchMediaLists(userName, language);
        const additionalCharacters = await fetchAdditionalCharacters(userName, language);

        // add initial media data to lookup, flattening lists
        for (const userMedia of mediaLists.lists.flatMap(list => list.entries).map(entry => mapUserMedia(entry))) {
            lookup[userMedia.id] = userMedia;
        }

        // add additional characters from all pages
        for (const mediaList of additionalCharacters) {
            for (const entry of mediaList.lists.flatMap(list => list.entries)) {
                const userMedia = lookup[entry.media.id];
                if (userMedia) {
                    userMedia.characters = [
                        ...(userMedia.characters || []),
                        ...entry.media.characters.edges.map(mapCharacter)
                    ];
                }
            }
        }

        // Convert lookup to array
        const userMediaList: UserMedia[] = Object.values(lookup);
        return userMediaList;
    },
}
